import { useEffect, useRef } from "react";

/**
 * Triggers an animate.css animation the first time the element scrolls into view.
 * The element is hidden (opacity: 0) until then, so it never flashes before animating.
 *
 * @param animation  animate.css class  e.g. "animate__fadeInUp"
 * @param delay      ms before the animation starts after becoming visible
 * @param threshold  0–1 fraction of element that must be visible
 * @param duration   animation duration in ms (default 750)
 */
export default function useAnimateCss(
  animation: string,
  delay = 0,
  threshold = 0.12,
  duration = 750,
) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.opacity = "0";
    el.style.setProperty("--animate-duration", `${duration}ms`);
    if (delay > 0) el.style.animationDelay = `${delay}ms`;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = "";
          el.classList.add("animate__animated", animation);
          obs.unobserve(el);
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [animation, delay, threshold, duration]);

  return ref;
}

/**
 * Staggered variant — animates each direct child of the target element
 * with an incremental delay so they cascade in one after another.
 *
 * @param animation   animate.css class  e.g. "animate__fadeInUp"
 * @param staggerMs   delay increment between each child (ms)
 * @param threshold   fraction of parent visible before triggering
 * @param duration    per-child animation duration (ms)
 */
export function useAnimateCssStagger(
  animation: string,
  staggerMs = 110,
  threshold = 0.08,
  duration = 620,
) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const children = Array.from(el.children) as HTMLElement[];
    children.forEach((c) => {
      c.style.opacity = "0";
      c.style.setProperty("--animate-duration", `${duration}ms`);
    });

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          children.forEach((c, i) => {
            c.style.opacity = "";
            c.style.animationDelay = `${i * staggerMs}ms`;
            c.classList.add("animate__animated", animation);
          });
          obs.unobserve(el);
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [animation, staggerMs, threshold, duration]);

  return ref;
}
